/**
 * Route Constants
 *
 * Centralized route definitions organized by sidebar navigation hierarchy.
 * Structure follows the new sidebar groups for intuitive navigation.
 *
 * @example
 * ```tsx
 * import { ROUTES } from '@/routes/routes';
 *
 * <Link to={ROUTES.ADMIN.PEOPLE.EMPLOYEES}>Employees</Link>
 * navigate(ROUTES.MY_WORK.DOCUMENTS);
 * ```
 */

export const ROUTES = {
  // ============================================
  // Public Routes
  // ============================================
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register-owner',
  JOIN_COMPANY: '/join-company',
  JOIN_REQUEST_STATUS: (tenantId: string) => `/join-request-status/${tenantId}`,
  INVITE: (token: string) => `/invite/${token}`,

  // Public website pages
  CONTACT: '/contact',
  ABOUT: '/about',
  FEATURES: '/features',
  PRICING: '/pricing',

  // Legal pages
  PRIVACY: '/privacy',
  TERMS: '/terms',
  DPA: '/dpa',
  COOKIES: '/cookies',
  IMPRINT: '/imprint',

  // Error pages
  UNAUTHORIZED: '/unauthorized',
  NOT_FOUND: '/404',

  // Kiosk Terminal (Public - No auth required)
  KIOSK_TERMINAL: (tenantId: string) => `/kiosk/${tenantId}`,

  // ============================================
  // Hub Pages (Zone-based navigation)
  // ============================================
  HUBS: {
    MY_SPACE: '/dashboard/my-space',
    MY_TEAM: '/dashboard/my-team',
    ADMINISTRATION: '/dashboard/administration',
    DEV_CONSOLE: '/dashboard/dev-console',
  },

  // ============================================
  // HOME Group - Main dashboard views
  // ============================================
  DASHBOARD: '/dashboard',
  PEOPLE: '/dashboard/people',
  PEOPLE_PROFILE: (id: string) => `/dashboard/people/${id}`,
  ANNOUNCEMENTS: '/dashboard/announcements',
  ASSISTANT: '/dashboard/assistant',
  HELP: '/dashboard/help',

  // ============================================
  // MY WORK Group - Personal workspace
  // ============================================
  MY_WORK: {
    PROFILE: '/dashboard/profile',
    PROFILE_EDIT: '/dashboard/profile/edit',
    REVIEWS: '/dashboard/me/reviews',
    REVIEW_DETAIL: (id: string) => `/dashboard/me/reviews/${id}`,
    COMPENSATION: '/dashboard/compensation',
    DOCUMENTS: '/dashboard/me/documents',
    PAYSLIPS: '/dashboard/me/documents?tab=payslips',
    BENEFITS: '/dashboard/benefits/my-benefits',
    BENEFITS_ENROLLMENT: '/dashboard/benefits/enrollment',
    BENEFITS_DEPENDENTS: '/dashboard/benefits/dependents',
    // Expense Management (Phase 1.1 - German-compliant expense reporting)
    EXPENSES: '/dashboard/expenses',
    EXPENSE_NEW: '/dashboard/expenses/new',
    EXPENSE_DETAIL: (id: string) => `/dashboard/expenses/${id}`,
    // Goals & OKRs (Phase 1 - Employee goal tracking)
    GOALS: '/dashboard/me/goals',
    GOAL_DETAIL: (id: string) => `/dashboard/me/goals/${id}`,
    GOAL_NEW: '/dashboard/me/goals/new',
  },

  // ============================================
  // TIME & LEAVE Group - Time management
  // ============================================
  TIME_LEAVE: {
    TIME: '/dashboard/time',
    MY_SCHEDULE: '/dashboard/time/my-schedule',
    TIME_SCHEDULE: '/dashboard/time/schedule',
    LEAVE: '/dashboard/leave',
    LEAVE_DETAIL: (id: string) => `/dashboard/leave/${id}`,
    KNOWLEDGE_BASE: '/dashboard/knowledge-base',
    KNOWLEDGE_BASE_ARTICLE: (id: string) => `/dashboard/knowledge-base/${id}`,
    LEARNING: '/dashboard/learning',
    LEARNING_COURSE: (id: string) => `/dashboard/learning/${id}`,
    CERTIFICATES: '/dashboard/learning?tab=certificates',
  },

  // ============================================
  // SCHEDULING Group - Shift planning & templates
  // ============================================
  SCHEDULING: {
    BASE: '/dashboard/scheduling',
    WEEKLY: '/dashboard/scheduling/weekly',
    TEMPLATES: '/dashboard/scheduling/templates',
    OVERVIEW: '/dashboard/scheduling/overview',
  },

  // ============================================
  // MY TEAM Group - Manager views
  // ============================================
  TEAM: {
    MEMBERS: '/dashboard/team',
    MEMBER_PROFILE: (id: string) => `/dashboard/team/${id}`,
    SCHEDULE: '/dashboard/time/schedule',
    SHIFT_APPROVALS: '/dashboard/time/approvals',
    LEAVE_APPROVALS: '/dashboard/leave/approvals',
    // Goals & OKRs (Phase 2 - Manager goal review)
    GOALS: '/dashboard/team/goals',
    GOAL_DETAIL: (id: string) => `/dashboard/team/goals/${id}`,
    GOAL_APPROVALS: '/dashboard/team/goals/approvals',
  },

  // ============================================
  // ADMIN Group - Administration
  // ============================================
  ADMIN: {
    BASE: '/dashboard/admin',

    // New Consolidated Settings (Sprint 1-2) - Modern restructured settings
    SETTINGS: {
      BASE: '/dashboard/admin/settings',
      COMPANY: '/dashboard/admin/settings/company',
      TIME_ATTENDANCE: '/dashboard/admin/settings/time-attendance',
      // Sprint 2 - Organization & People
      ORGANIZATION: '/dashboard/admin/settings/organization',
      // Feature 2.1 - Full Company Data Export
      EXPORT: '/dashboard/admin/settings/export',
      // Integrations - Webhooks and external system connections
      INTEGRATIONS: '/dashboard/admin/settings/integrations',
      // Future consolidated settings pages (Sprint 2-4)
      // PEOPLE: '/dashboard/admin/settings/people',
      // HR: '/dashboard/admin/settings/hr',
      // CONTENT: '/dashboard/admin/settings/content',
      // SYSTEM: '/dashboard/admin/settings/system',
    },

    // Company Settings sub-group (Legacy - to be deprecated)
    COMPANY: {
      BASE: '/dashboard/admin/company',
      DEPARTMENTS: '/dashboard/admin/company/departments',
      HOLIDAYS: '/dashboard/admin/company/holidays',
    },

    // People Management sub-group
    PEOPLE: {
      BASE: '/dashboard/admin/people',
      EMPLOYEE: (id: string) => `/dashboard/people/${id}`,
      ROLES: '/dashboard/admin/people/roles',
      PERMISSIONS: '/dashboard/admin/people/permissions',
      ALUMNI: '/dashboard/admin/people/alumni',
    },

    // Leave & Time Config sub-group
    LEAVE_TIME: {
      BASE: '/dashboard/admin/leave-time',
      POLICIES: '/dashboard/admin/leave-time/policies',
      TIME_SETTINGS: '/dashboard/admin/leave-time/settings',
    },

    // Content Management sub-group
    CONTENT: {
      BASE: '/dashboard/admin/content',
      // Knowledge Base Hub - TRUE CONSOLIDATION
      // Consolidated hub with URL-based tabs: documents (default), categories, analytics
      KNOWLEDGE_BASE: '/dashboard/admin/knowledge-base',
      KNOWLEDGE_BASE_DOCUMENTS: '/dashboard/admin/knowledge-base', // Default tab
      KNOWLEDGE_BASE_CATEGORIES: '/dashboard/admin/knowledge-base?tab=categories',
      KNOWLEDGE_BASE_ANALYTICS: '/dashboard/admin/knowledge-base?tab=analytics',
      KNOWLEDGE_BASE_NEW: '/dashboard/admin/knowledge-base/documents/new',
      KNOWLEDGE_BASE_EDIT: (id: string) => `/dashboard/admin/knowledge-base/documents/${id}/edit`,
      // Training Management
      TRAINING: '/dashboard/admin/content/training',
      TRAINING_COURSE_NEW: '/dashboard/admin/content/training/courses/new',
      TRAINING_COURSE_EDIT: (id: string) => `/dashboard/admin/content/training/courses/${id}/edit`,
    },

    // HR Operations sub-group
    HR: {
      BASE: '/dashboard/admin/hr',
      REVIEWS: '/dashboard/admin/hr/reviews',
      REVIEW_CYCLES: '/dashboard/admin/hr/reviews/cycles',
      // Note: Compensation routes are under ADMIN.COMPENSATION (see below)
      RECRUITING: '/dashboard/recruiting/jobs',
      RECRUITING_JOB: (id: string) => `/dashboard/recruiting/jobs/${id}`,
      RECRUITING_JOB_NEW: '/dashboard/recruiting/jobs/new',
      // Asset Management
      ASSETS: '/dashboard/admin/assets',
      ASSET_DETAIL: (id: string) => `/dashboard/admin/assets/${id}`,
      // Expense Management Hub + sub-pages
      EXPENSE_HUB: '/dashboard/admin/expenses',
      EXPENSE_ANALYTICS: '/dashboard/admin/hr/expenses/analytics',
      EXPENSE_SETTINGS: '/dashboard/admin/hr/expenses/settings',
      // Goals & OKRs (Phase 2 - HR Admin management)
      GOALS: '/dashboard/admin/hr/goals',
      GOAL_CYCLES: '/dashboard/admin/hr/goals/cycles',
      GOAL_DETAIL: (id: string) => `/dashboard/admin/hr/goals/${id}`,
    },

    // Benefits Administration Hub - TRUE CONSOLIDATION
    // Consolidated hub with URL-based tabs: types (default), enrollments
    BENEFITS: {
      BASE: '/dashboard/admin/hr/benefits',
      TYPES: '/dashboard/admin/hr/benefits', // Default tab
      ENROLLMENTS: '/dashboard/admin/hr/benefits?tab=enrollments',
      TYPE_NEW: '/dashboard/admin/hr/benefits/types/new',
      TYPE_EDIT: (id: string) => `/dashboard/admin/hr/benefits/types/${id}/edit`,
    },

    // Standalone admin items
    BRANDING: '/dashboard/settings/branding',
    BILLING: '/dashboard/billing',
    PROMO_CODES: '/dashboard/admin/promo-codes', // Internal admin: @etchare.com only
    FOUNDING_CUSTOMERS: '/dashboard/admin/founding-customers', // Internal admin: @etchare.com only
    REPORTS: '/dashboard/reports',
    REPORTS_ATTENDANCE: '/dashboard/reports/attendance',
    REPORTS_TIME: '/dashboard/reports/time',
    AUDIT_LOG: '/dashboard/admin/audit-log',
    SYSTEM_MONITORING: '/dashboard/admin/system-monitoring',
    PAYROLL: '/dashboard/admin/hr/compensation/payroll',

    // ============================================
    // Legacy aliases for backward compatibility
    // These will be removed after migration is complete
    // ============================================

    // Legacy EMPLOYEES alias (now under PEOPLE)
    EMPLOYEES: '/dashboard/admin/people',
    EMPLOYEE: (id: string) => `/dashboard/admin/people/${id}`,

    // Compensation Management routes (primary definition)
    COMPENSATION: {
      BASE: '/dashboard/admin/hr/compensation',
      // Compensation Hub (new dedicated hub — Phase 2)
      HUB: '/dashboard/admin/compensation',
      COMP_OVERVIEW: '/dashboard/admin/compensation',
      COMP_BONUSES: '/dashboard/admin/compensation?tab=bonuses',
      COMP_TIP_POOLS: '/dashboard/admin/compensation?tab=tip-pools',
      COMP_COMMISSIONS: '/dashboard/admin/compensation?tab=commissions',
      COMP_ANALYTICS: '/dashboard/admin/compensation?tab=analytics',
      DISCLAIMER: '/dashboard/admin/hr/compensation/disclaimer',
      SETTINGS: '/dashboard/admin/hr/compensation/settings',
      ANALYTICS: '/dashboard/admin/hr/compensation/analytics',
      REPORTS: '/dashboard/admin/hr/compensation/reports',
      PLANS: '/dashboard/admin/hr/compensation/plans',
      PLAN_NEW: '/dashboard/admin/hr/compensation/plans/new',
      PLAN_EDIT: (id: string) => `/dashboard/admin/hr/compensation/plans/${id}/edit`,
      PLAN_VIEW: (id: string) => `/dashboard/admin/hr/compensation/plans/${id}`,
      BONUSES: '/dashboard/admin/hr/compensation/bonuses',
      BONUS_DETAIL: (id: string) => `/dashboard/admin/hr/compensation/bonuses/${id}`,
      BONUS_VIEW: (id: string) => `/dashboard/admin/hr/compensation/bonuses/${id}`,
      TIP_POOLS: '/dashboard/admin/hr/compensation/tip-pools',
      TIP_POOL_NEW: '/dashboard/admin/hr/compensation/tip-pools/new',
      TIP_POOL_VIEW: (id: string) => `/dashboard/admin/hr/compensation/tip-pools/${id}`,
    },

    // Kiosk Hub - Consolidated with URL-based tabs
    KIOSK: {
      BASE: '/dashboard/admin/kiosk',
      OVERVIEW: '/dashboard/admin/kiosk',
      PINS: '/dashboard/admin/kiosk?tab=pins',
      MONITORING: '/dashboard/admin/kiosk?tab=monitoring',
      SETTINGS: '/dashboard/admin/kiosk?tab=settings',
    },

    // Leave Settings sub-group (Leave policies & calendars)
    LEAVE_SETTINGS: {
      BASE: '/dashboard/admin/leave-settings',
      POLICIES: '/dashboard/admin/leave-settings/policies',
      CALENDARS: '/dashboard/admin/leave-settings/calendars',
    },

    // Document Hub (HR Admin document management)
    DOCUMENT_HUB: '/dashboard/admin/documents',

    // Compliance Dashboard (Regulatory monitoring & violations)
    COMPLIANCE: '/dashboard/admin/compliance',
  },

  // ============================================
  // DEVELOPER Group - Developer tools
  // ============================================
  DEV: {
    CONSOLE: '/dev/console',
    BRANDING_CONTROL: '/dashboard/admin/branding-control',
    FEEDBACK: '/dashboard/admin/feedback',
    CMS: '/dashboard/admin/cms',
    DEVELOPER_ACCESS: '/dashboard/admin/developer-access',
  },

  // ============================================
  // Setup & Onboarding (Special flows)
  // ============================================
  SETUP: '/dashboard/setup',
  WELCOME: '/dashboard/welcome',

  // ============================================
  // Legacy route aliases (for backward compatibility)
  // These map old routes to new structure
  // ============================================
  // Keep old references working during migration
  LEAVE: '/dashboard/leave',
  TIME: '/dashboard/time',
  DOCUMENTS: '/dashboard/me/documents',
  PAYSLIPS: '/dashboard/me/documents?tab=payslips',
  ACTIVITY: '/dashboard/activity',
  KNOWLEDGE_BASE: '/dashboard/knowledge-base',
  PROFILE: '/dashboard/profile',
  SETTINGS: '/dashboard/settings',
  COMPENSATION: '/dashboard/compensation',
  LEAVE_DETAILS: (id: string) => `/dashboard/leave/${id}`,
  LEAVE_APPROVALS: '/dashboard/leave/approvals',
  TIME_APPROVALS: '/dashboard/time/approvals',
  TIME_SETTINGS: '/dashboard/time/settings',
  MY_TEAM: '/dashboard/team',
  SHIFT_APPROVALS: '/dashboard/time/approvals',
  TEAM_TIME: '/dashboard/team-time',

  // Training/LMS references
  TRAINING: {
    // Learning Hub - Consolidated with URL-based tabs
    HUB: '/dashboard/learning',
    MY_LEARNING: '/dashboard/learning',
    CATALOG: '/dashboard/learning?tab=catalog',
    MY_CERTIFICATES: '/dashboard/learning?tab=certificates',
    MY_QUIZ_HISTORY: '/dashboard/learning?tab=quiz-history',
    // Course detail and player routes (under /training prefix)
    COURSE_DETAIL: (courseId: string) => `/training/catalog/${courseId}`,
    COURSE_PLAYER: (courseId: string) => `/training/player/${courseId}`,
    // Quiz routes
    QUIZ_PLAYER: (quizId: string) => `/training/quiz/${quizId}`,
    QUIZ_PLAYER_WITH_ENROLLMENT: (quizId: string, enrollmentId: string) => `/training/quiz/${quizId}/enrollment/${enrollmentId}`,
    QUIZ_RESULTS: (enrollmentId: string, quizId: string, attemptNumber: number) => `/training/enrollment/${enrollmentId}/quiz/${quizId}/results/${attemptNumber}`,
    // Team/Manager routes
    TEAM_QUIZ_PROGRESS: '/training/team-quiz-progress',
    // Admin routes
    ADMIN: '/dashboard/admin/training',
    COURSE_BUILDER: '/dashboard/admin/training/courses/new',
    COURSE_EDIT: (courseId: string) => `/dashboard/admin/training/courses/${courseId}/edit`,
    QUIZ_BUILDER: (courseId: string) => `/dashboard/admin/training/courses/${courseId}/quiz/new`,
    QUIZ_EDIT: (courseId: string, quizId: string) => `/dashboard/admin/training/courses/${courseId}/quiz/${quizId}/edit`,
    QUIZ_ANALYTICS: (courseId: string, quizId: string) => `/dashboard/admin/training/courses/${courseId}/quiz/${quizId}/analytics`,
    ANALYTICS: '/dashboard/admin/training/analytics',
    SETTINGS: '/dashboard/admin/training/settings',

    // Route patterns for router definitions (use these in router.tsx)
    // These use :param syntax for React Router matching
    PATTERNS: {
      COURSE_DETAIL: '/training/catalog/:courseId',
      COURSE_PLAYER: '/training/player/:courseId',
      QUIZ_PLAYER: '/training/quiz/:quizId',
      QUIZ_PLAYER_WITH_ENROLLMENT: '/training/quiz/:quizId/enrollment/:enrollmentId',
      QUIZ_RESULTS: '/training/enrollment/:enrollmentId/quiz/:quizId/results/:attemptNumber',
      COURSE_EDIT: '/dashboard/admin/training/courses/:courseId/edit',
      QUIZ_BUILDER: '/dashboard/admin/training/courses/:courseId/quiz/new',
      QUIZ_EDIT: '/dashboard/admin/training/courses/:courseId/quiz/:quizId/edit',
      QUIZ_ANALYTICS: '/dashboard/admin/training/courses/:courseId/quiz/:quizId/analytics',
    },
  },

  // Finance Hub routes
  // Finance Hub - Consolidated with URL-based tabs
  FINANCE: {
    HUB: '/dashboard/admin/finance',
    OVERVIEW: '/dashboard/admin/finance',
    BONUSES: '/dashboard/admin/finance?tab=bonuses',
    TIP_POOLS: '/dashboard/admin/finance?tab=tip-pools',
    COMMISSIONS: '/dashboard/admin/finance?tab=commissions',
    PAYROLL: '/dashboard/admin/finance?tab=payroll',
    ANALYTICS: '/dashboard/admin/finance?tab=analytics',
    // Payroll export disclaimer (one-time acknowledgment gate)
    PAYROLL_DISCLAIMER: '/dashboard/admin/finance/disclaimer',
  },

  // Legacy PERFORMANCE_REVIEWS references
  PERFORMANCE_REVIEWS: {
    MY_REVIEWS: '/dashboard/me/reviews',
    SELF_ASSESSMENT: (reviewId: string) => `/dashboard/me/reviews/self-assessment/${reviewId}`,
    DETAIL: (reviewId: string) => `/dashboard/me/reviews/${reviewId}`,
    MANAGER_REVIEW: (reviewId: string) => `/dashboard/admin/performance-reviews/review/${reviewId}`,
    DELIVER: (reviewId: string) => `/dashboard/admin/performance-reviews/deliver/${reviewId}`,
    ADMIN: '/dashboard/admin/performance-reviews',
    REVIEW_CYCLES: '/dashboard/admin/performance-reviews/cycles',
  },

  // ============================================
  // SURVEYS - Employee Engagement Surveys
  // ============================================
  SURVEYS: {
    // Employee routes - View and take assigned surveys
    MY_SURVEYS: '/dashboard/me/surveys',
    TAKE: (responseToken: string) => `/surveys/take/${responseToken}`,
    // Admin routes - Create and manage surveys
    ADMIN: '/dashboard/surveys',
    NEW: '/dashboard/surveys/new',
    ANALYTICS: (surveyId: string) => `/dashboard/surveys/${surveyId}/analytics`,
    // Route patterns for router definitions
    PATTERNS: {
      TAKE: '/surveys/take/:responseToken',
      ANALYTICS: '/dashboard/surveys/:surveyId/analytics',
    },
  },
} as const;

/**
 * Type-safe route helper for building dynamic routes
 */
export type RouteParams = {
  employeeId?: string;
  leaveId?: string;
  documentId?: string;
  requestId?: string;
  token?: string;
  courseId?: string;
  lessonId?: string;
  reviewId?: string;
};

/**
 * Legacy route redirects (for backward compatibility)
 *
 * NOTE: This mapping is NOT consumed by the router — redirects are
 * hardcoded individually in router.tsx. This serves as documentation only.
 * Only include entries where the "from" URL is genuinely retired
 * (not used as a canonical path in ROUTES or navigationConfig).
 */
export const LEGACY_ROUTE_REDIRECTS: Record<string, string> = {
  // Old employee routes
  '/dashboard/employees': '/dashboard/admin/people',
  '/dashboard/admin/employees': '/dashboard/admin/people',

  // Old directory routes
  '/dashboard/directory': '/dashboard/people',
  '/dashboard/colleagues': '/dashboard/people',

  // Old admin routes → New consolidated Settings pages
  '/dashboard/admin/policies': '/dashboard/admin/settings/time-attendance',
  '/dashboard/admin/holidays': '/dashboard/admin/settings/time-attendance',
  '/dashboard/admin/organization': '/dashboard/admin/settings/organization',
  '/dashboard/admin/time-tracking': '/dashboard/admin/settings/time-attendance',

  // Old roles route → Now in Organization Structure settings
  '/dashboard/roles': '/dashboard/admin/settings/organization',

  // Old personal routes
  '/dashboard/payslips': '/dashboard/me/documents?tab=payslips',
  '/dashboard/reviews': '/dashboard/me/reviews',

  // Old training routes
  '/training/catalog': '/dashboard/learning',
  '/training/my-learning': '/dashboard/learning',
  '/training/certificates': '/dashboard/learning/certificates',
};

/**
 * Legacy routes object for backward compatibility
 * Used in router.tsx for legacy route redirects
 */
export const LEGACY_ROUTES = {
  EMPLOYEES_OLD: '/dashboard/employees',
};
